<template>
  <div class="track-schema">
    <div class="track-schema__header">
      <div class="track-schema__title">
        <b-icon
          icon="signpost-split"
          class="mr-1"
        />
        Схема трассы
        <b-badge
          variant="warning"
          class="ml-2 track-schema__beta-badge"
          title="Функция в бета-тестировании: логика отображения и статусы ещё дорабатываются"
        >
          Бета
        </b-badge>
        <b-badge
          variant="secondary"
          class="ml-2 track-schema__count-badge"
        >
          {{ orderedNodes.length }} точ.
        </b-badge>
      </div>

      <div
        v-if="orderedNodes.length > 1"
        class="track-schema__controls"
      >
        <b-button
          size="sm"
          variant="outline-primary"
          :disabled="isAnimating"
          @click="startAnimation"
        >
          <b-icon
            :icon="isAnimating ? 'arrow-repeat' : 'play-fill'"
            class="mr-1"
          />
          {{ isAnimating ? 'Анимация…' : 'Проиграть путь' }}
        </b-button>
        <b-button
          size="sm"
          variant="outline-secondary"
          :disabled="!isAnimating"
          @click="stopAnimation"
        >
          <b-icon
            icon="stop-fill"
            class="mr-1"
          />
          Стоп
        </b-button>
      </div>
    </div>

    <div class="track-schema__beta-note">
      <b-icon
        icon="exclamation-triangle"
        class="mr-1"
      />
      Бета-версия визуализации: схема строится автоматически из настроенных
      контрольных точек и может неточно отражать реальную геометрию трассы.
      Статусы и подсветка используются для общей проверки ожидаемого порядка
      прохождения и ещё дорабатываются.
    </div>

    <div
      v-if="orderedNodes.length === 0"
      class="track-schema__empty"
    >
      <b-icon
        icon="diagram-3"
        font-scale="1.4"
        class="mb-1"
      />
      <div>Нет настроенных контрольных точек.</div>
      <small class="text-muted">
        Добавьте точки во вкладке «Конфигурация» → «Трасса», чтобы увидеть схему.
      </small>
    </div>

    <div
      v-else
      class="track-schema__canvas-wrap"
    >
      <svg
        class="track-schema__svg"
        :viewBox="`0 0 ${viewWidth} ${viewHeight}`"
        preserveAspectRatio="xMidYMid meet"
        role="img"
        aria-label="Схема трассы"
      >
        <!-- Shared-reader connectors: dashed thin lines linking checkpoints
             that use the same physical chip reader (e.g. out-and-back). -->
        <g
          v-if="sharedReaderLinks.length > 0"
          class="track-schema__reader-links"
        >
          <line
            v-for="link in sharedReaderLinks"
            :key="'rl-' + link.key"
            :x1="link.x1"
            :y1="link.y1"
            :x2="link.x2"
            :y2="link.y2"
            class="track-schema__reader-link"
          />
        </g>

        <!-- Dashed base route (full) -->
        <path
          ref="routePathEl"
          :d="routePath"
          class="track-schema__path-base"
          fill="none"
        />

        <!-- Completed section of route -->
        <path
          v-if="completedPath"
          :d="completedPath"
          class="track-schema__path-done"
          fill="none"
        />

        <!-- Lap separators (subtle vertical guides where lap changes) -->
        <g
          v-for="sep in lapSeparators"
          :key="'sep-' + sep.index"
        >
          <line
            :x1="sep.x"
            :x2="sep.x"
            :y1="sep.yTop"
            :y2="sep.yBottom"
            class="track-schema__lap-sep"
          />
          <text
            :x="sep.x"
            :y="sep.yTop - 6"
            text-anchor="middle"
            class="track-schema__lap-label"
          >
            Круг {{ sep.toLap }}
          </text>
        </g>

        <!-- Checkpoint nodes -->
        <g
          v-for="(node, idx) in visibleNodes"
          :key="'node-' + idx"
          :class="['track-schema__node-group', node.stateClass, { 'is-merged': idx === 0 && isClosedLoop }]"
        >
          <!-- Outer pulse for active node -->
          <circle
            v-if="node.index === activeNodeIndex || (isClosedLoop && idx === 0 && activeNodeIndex === orderedNodes.length - 1)"
            :cx="node.x"
            :cy="node.y"
            r="22"
            class="track-schema__node-pulse"
          />
          <!-- Main node circle -->
          <circle
            :cx="node.x"
            :cy="node.y"
            :r="isClosedLoop && idx === 0 ? 18 : (node.isSpecial ? 16 : 13)"
            class="track-schema__node"
          />
          <!-- Node index label -->
          <text
            :x="node.x"
            :y="node.y + 4"
            text-anchor="middle"
            class="track-schema__node-index"
          >
            {{ idx + 1 }}
          </text>

          <!-- Top label: header / point -->
          <text
            :x="node.x"
            :y="node.y - 42"
            text-anchor="middle"
            class="track-schema__node-label"
          >
            {{ truncate(node.mergedHeader || node.header, 22) }}
          </text>
          <text
            :x="node.x"
            :y="node.y - 26"
            text-anchor="middle"
            class="track-schema__node-sub"
          >
            {{ node.point }}<template v-if="node.reserv"> / {{ node.reserv }}</template>
          </text>

          <!-- Bottom labels: lap / time / actual time -->
          <text
            :x="node.x"
            :y="node.y + 34"
            text-anchor="middle"
            class="track-schema__node-time"
          >
            {{ node.expectedTimeLabel }}
          </text>
          <text
            v-if="node.actualTimeLabel || node.mergedActualTimeLabel"
            :x="node.x"
            :y="node.y + 50"
            text-anchor="middle"
            class="track-schema__node-actual"
          >
            {{ node.mergedActualTimeLabel || node.actualTimeLabel }}
          </text>
          <text
            v-if="node.badgeText"
            :x="node.x"
            :y="node.y + 66"
            text-anchor="middle"
            class="track-schema__node-badge"
            :class="'track-schema__node-badge--' + node.badgeVariant"
          >
            {{ node.badgeText }}
          </text>
          <!-- Shared reader hint (only when this checkpoint's point is used
               for 2+ checkpoints on the track; not rendered for the merged
               start/finish node). -->
          <text
            v-if="sharedReaderInfo[node.index] && !(idx === 0 && isClosedLoop)"
            :x="node.x"
            :y="node.y + (node.badgeText ? 82 : 66)"
            text-anchor="middle"
            class="track-schema__reader-hint"
          >
            общий ридер · {{ sharedReaderInfo[node.index].order }}/{{ sharedReaderInfo[node.index].total }}
          </text>
        </g>

        <!-- Animated runner marker -->
        <g
          v-if="orderedNodes.length > 1 && markerXY"
          class="track-schema__runner"
        >
          <circle
            :cx="markerXY.x"
            :cy="markerXY.y"
            r="10"
            class="track-schema__runner-halo"
          />
          <circle
            :cx="markerXY.x"
            :cy="markerXY.y"
            r="6"
            class="track-schema__runner-dot"
          />
        </g>
      </svg>

      <div class="track-schema__legend">
        <div class="track-schema__legend-item">
          <span class="dot dot--done" /> Пройдено
        </div>
        <div class="track-schema__legend-item">
          <span class="dot dot--current" /> Текущее
        </div>
        <div class="track-schema__legend-item">
          <span class="dot dot--pending" /> Ожидается
        </div>
        <div class="track-schema__legend-item">
          <span class="dot dot--missing" /> Пропуск
        </div>
        <div class="track-schema__legend-item">
          <span class="dot dot--violation" /> Нарушение
        </div>
        <div class="track-schema__legend-item">
          <span class="dot dot--overridden" /> Переопределено
        </div>
        <div
          v-if="sharedReaderLinks.length > 0"
          class="track-schema__legend-item"
          title="Пунктирная сиреневая линия соединяет чекпоинты, которые используют один и тот же физический ридер (например, «туда-обратно» мимо одной антенны)"
        >
          <span class="legend-dash" /> Общий ридер
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { BIcon, BButton, BBadge } from 'bootstrap-vue'

function parseDurationToMs(str) {
  if (str == null || str === '') return null
  const s = String(str).trim()
  if (!s || s === '—') return null
  const parts = s.split(':').map((p) => p.trim())
  if (parts.length === 3) {
    const h = parseInt(parts[0], 10) || 0
    const m = parseInt(parts[1], 10) || 0
    const secParts = parts[2].split('.')
    const sec = parseInt(secParts[0], 10) || 0
    const frac = secParts[1] != null ? String(secParts[1]).replace(/\D/g, '') : ''
    const ms = parseInt(`${frac}000`.slice(0, 3), 10) || 0
    return ((h * 60 + m) * 60 + sec) * 1000 + ms
  }
  if (parts.length === 2) {
    const m = parseInt(parts[0], 10) || 0
    const secParts = parts[1].split('.')
    const sec = parseInt(secParts[0], 10) || 0
    const frac = secParts[1] != null ? String(secParts[1]).replace(/\D/g, '') : ''
    const ms = parseInt(`${frac}000`.slice(0, 3), 10) || 0
    return (m * 60 + sec) * 1000 + ms
  }
  const asNum = Number(s)
  if (Number.isFinite(asNum)) return Math.round(asNum * 1000)
  return null
}

function msToHuman(ms) {
  if (ms == null || !Number.isFinite(ms)) return ''
  const sign = ms < 0 ? '-' : ''
  const total = Math.abs(Math.round(ms / 1000))
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = total % 60
  if (h > 0) {
    return `${sign}${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
  }
  return `${sign}${m}:${String(s).padStart(2, '0')}`
}

function normalizePoint(p) {
  return String(p == null ? '' : p).trim().toUpperCase()
}

/**
 * Arrange `count` positions evenly on an ellipse. Start at the top (12 o'clock)
 * and go clockwise. Used when the track is a closed loop (start point equals
 * finish point), so the path visually forms a ring.
 */
function buildEllipseLayout(count, viewWidth, viewHeight, padding) {
  const points = []
  if (count <= 0) return points
  const cx = viewWidth / 2
  const cy = viewHeight / 2
  const rx = (viewWidth - padding * 2) / 2
  const ry = (viewHeight - padding * 2) / 2
  for (let i = 0; i < count; i += 1) {
    const angle = -Math.PI / 2 + (2 * Math.PI * i) / count
    points.push({
      x: cx + rx * Math.cos(angle),
      y: cy + ry * Math.sin(angle),
      row: 0,
      angle,
    })
  }
  return points
}

/**
 * Smooth closed loop path through `points` (using Catmull-Rom → cubic Bezier
 * conversion). Returns an SVG path string starting at `points[0]` and closing
 * back to it. Falls back to a polygon for 3 or fewer points.
 */
function buildClosedLoopPath(points) {
  const n = points.length
  if (n === 0) return ''
  if (n === 1) return `M ${points[0].x} ${points[0].y}`
  if (n <= 3) {
    const first = points[0]
    const rest = points.slice(1).map((p) => `L ${p.x} ${p.y}`).join(' ')
    return `M ${first.x} ${first.y} ${rest} Z`
  }
  const parts = [`M ${points[0].x} ${points[0].y}`]
  const tension = 0.5
  for (let i = 0; i < n; i += 1) {
    const p0 = points[(i - 1 + n) % n]
    const p1 = points[i]
    const p2 = points[(i + 1) % n]
    const p3 = points[(i + 2) % n]
    const c1x = p1.x + (p2.x - p0.x) * tension / 3
    const c1y = p1.y + (p2.y - p0.y) * tension / 3
    const c2x = p2.x - (p3.x - p1.x) * tension / 3
    const c2y = p2.y - (p3.y - p1.y) * tension / 3
    parts.push(`C ${c1x} ${c1y}, ${c2x} ${c2y}, ${p2.x} ${p2.y}`)
  }
  parts.push('Z')
  return parts.join(' ')
}

/**
 * Smooth open path from the first up to and including the `toIndex`-th point
 * of `points`, following the same Catmull-Rom tangents as `buildClosedLoopPath`
 * so the completed segment visually matches the base loop. Used for the
 * "completed" portion of the closed track.
 */
function buildClosedLoopPartialPath(points, toIndex) {
  const n = points.length
  if (n === 0 || toIndex <= 0) return ''
  const parts = [`M ${points[0].x} ${points[0].y}`]
  const tension = 0.5
  const end = Math.min(toIndex, n)
  for (let i = 0; i < end; i += 1) {
    const p0 = points[(i - 1 + n) % n]
    const p1 = points[i % n]
    const p2 = points[(i + 1) % n]
    const p3 = points[(i + 2) % n]
    const c1x = p1.x + (p2.x - p0.x) * tension / 3
    const c1y = p1.y + (p2.y - p0.y) * tension / 3
    const c2x = p2.x - (p3.x - p1.x) * tension / 3
    const c2y = p2.y - (p3.y - p1.y) * tension / 3
    parts.push(`C ${c1x} ${c1y}, ${c2x} ${c2y}, ${p2.x} ${p2.y}`)
  }
  return parts.join(' ')
}

function buildSerpentineLayout(count, viewWidth, viewHeight, padding, nodesPerRow) {
  const points = []
  if (count <= 0) return points
  const innerW = viewWidth - padding * 2
  const innerH = viewHeight - padding * 2
  const rows = Math.max(1, Math.ceil(count / nodesPerRow))
  const rowHeight = rows > 1 ? innerH / (rows - 1) : 0

  for (let i = 0; i < count; i += 1) {
    const row = Math.floor(i / nodesPerRow)
    const idxInRow = i - row * nodesPerRow
    const itemsInThisRow = Math.min(nodesPerRow, count - row * nodesPerRow)
    const leftToRight = row % 2 === 0
    const effectiveColWidth = itemsInThisRow > 1 ? innerW / (itemsInThisRow - 1) : 0
    const xLtr = padding + effectiveColWidth * idxInRow
    const xRtl = padding + innerW - effectiveColWidth * idxInRow
    const x = itemsInThisRow === 1 ? padding + innerW / 2 : (leftToRight ? xLtr : xRtl)
    const y = padding + rowHeight * row
    points.push({ x, y, row, col: idxInRow, leftToRight })
  }
  return points
}

/**
 * Within a row we draw a straight line; between rows we use a cubic Bezier
 * with horizontal tangents at both endpoints so the curve stays in the gap
 * between rows and does not pass through label zones above/below nodes.
 */
function buildSmoothPath(points) {
  if (!points || points.length === 0) return ''
  if (points.length === 1) return `M ${points[0].x} ${points[0].y}`
  const parts = [`M ${points[0].x} ${points[0].y}`]
  for (let i = 1; i < points.length; i += 1) {
    const prev = points[i - 1]
    const cur = points[i]
    if (prev.row === cur.row) {
      parts.push(`L ${cur.x} ${cur.y}`)
      continue
    }
    const dx = cur.x - prev.x
    const tangent = Math.max(60, Math.abs(dx) * 0.6)
    const dir = dx >= 0 ? 1 : -1
    const c1x = prev.x + dir * tangent
    const c1y = prev.y
    const c2x = cur.x - dir * tangent
    const c2y = cur.y
    parts.push(`C ${c1x} ${c1y}, ${c2x} ${c2y}, ${cur.x} ${cur.y}`)
  }
  return parts.join(' ')
}

function pointsDistance(a, b) {
  const dx = a.x - b.x
  const dy = a.y - b.y
  return Math.sqrt(dx * dx + dy * dy)
}

function interpolateAlong(points, t) {
  if (!points || points.length === 0) return null
  if (points.length === 1) return { x: points[0].x, y: points[0].y }
  const segLens = []
  let total = 0
  for (let i = 1; i < points.length; i += 1) {
    const d = pointsDistance(points[i - 1], points[i])
    segLens.push(d)
    total += d
  }
  if (total === 0) return { x: points[0].x, y: points[0].y }
  const clamped = Math.max(0, Math.min(1, t))
  const target = clamped * total
  let acc = 0
  for (let i = 0; i < segLens.length; i += 1) {
    const len = segLens[i]
    if (acc + len >= target || i === segLens.length - 1) {
      const localT = len === 0 ? 0 : (target - acc) / len
      const a = points[i]
      const b = points[i + 1]
      return {
        x: a.x + (b.x - a.x) * localT,
        y: a.y + (b.y - a.y) * localT,
      }
    }
    acc += len
  }
  const last = points[points.length - 1]
  return { x: last.x, y: last.y }
}

/**
 * Build ordered checkpoint nodes enriched with participant progress state.
 *
 * States used for highlight:
 *   done      — actual time recorded (either from checkpointResults or override).
 *   current   — last completed node; animated pulse marker.
 *   pending   — not yet reached in expected order.
 *   missing   — skipped: later checkpoints already have a reading but this one has none.
 *   violation — violatesTimeConstraint or actual order differs from expected.
 *   overridden— isOverridden (manual edit).
 */
function buildNodes(checkpoints, participant) {
  const list = (checkpoints || []).filter((cp) => cp && (cp.point || cp.header))
  const cpResults = (participant && participant.checkpointResults) || []

  const findResult = (cp) => cpResults.find(
    (r) => normalizePoint(r.point) === normalizePoint(cp.point)
      && (r.lap || 0) === (cp.lap || 0),
  ) || null

  const withReading = list.map((cp) => {
    const r = findResult(cp)
    const tDisplay = r ? [r.displayTime, r.splitTime, r.time]
      .map((x) => (x != null ? String(x).trim() : ''))
      .find((s) => s !== '' && s !== '—') : null
    const hasReading = !!(r && (r.isOverridden || (tDisplay && tDisplay !== '—')))
    const cumulativeMs = r && r.time ? parseDurationToMs(r.time) : null
    return {
      cp,
      result: r,
      hasReading,
      displayTime: tDisplay || (r && r.time) || null,
      cumulativeActualMs: cumulativeMs,
      isOverridden: !!(r && r.isOverridden),
      violates: !!(r && r.violatesTimeConstraint),
    }
  })

  let lastDoneIndex = -1
  for (let i = 0; i < withReading.length; i += 1) {
    if (withReading[i].hasReading) lastDoneIndex = i
  }

  // Any checkpoint before lastDoneIndex without reading is a "missing" (skip).
  // Compute previous-actual to verify monotonicity for violations.
  let prevActualMs = null
  return withReading.map((item, i) => {
    let state = 'pending'
    if (item.hasReading) state = 'done'
    if (i < lastDoneIndex && !item.hasReading) state = 'missing'
    if (i === lastDoneIndex) state = 'current'

    let orderViolation = false
    if (item.hasReading && item.cumulativeActualMs != null) {
      if (prevActualMs != null && item.cumulativeActualMs < prevActualMs - 1) {
        orderViolation = true
      }
      prevActualMs = item.cumulativeActualMs
    }

    const finalViolation = item.violates || orderViolation
    if (finalViolation && state === 'done') state = 'violation'
    if (finalViolation && state === 'current') state = 'violation-current'

    const expectedMs = parseDurationToMs(item.cp.time)
    const expectedTimeLabel = expectedMs != null
      ? msToHuman(expectedMs)
      : (item.cp.time != null && String(item.cp.time).trim() !== ''
        ? String(item.cp.time)
        : '')

    const actualTimeLabel = item.hasReading && item.displayTime
      ? item.displayTime
      : null

    let badgeText = null
    let badgeVariant = null
    if (item.isOverridden) {
      badgeText = 'ручн.'
      badgeVariant = 'overridden'
    } else if (finalViolation) {
      badgeText = 'наруш.'
      badgeVariant = 'violation'
    } else if (state === 'missing') {
      badgeText = 'пропуск'
      badgeVariant = 'missing'
    }

    return {
      index: i,
      point: item.cp.point || '',
      header: item.cp.header || item.cp.point || `Точка ${i + 1}`,
      lap: item.cp.lap || 0,
      reserv: item.cp.reserv || item.cp.backup || '',
      isSpecial: i === 0 || i === (withReading.length - 1),
      state,
      stateClass: `track-schema__state-${item.isOverridden ? 'overridden' : state}`,
      expectedTimeLabel,
      actualTimeLabel,
      badgeText,
      badgeVariant,
      hasReading: item.hasReading,
    }
  })
}

export default {
  name: 'TrackSchemaView',
  components: {
    BIcon,
    BButton,
    BBadge,
  },
  props: {
    checkpoints: {
      type: Array,
      default: () => [],
    },
    participant: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      viewWidth: 960,
      padding: 90,
      rowSpacing: 190,
      maxNodesPerRow: 6,
      animationT: 0,
      isAnimating: false,
      animationHandle: null,
      animationStartTs: null,
      animationDurationMs: 3500,
      markerXY: null,
    }
  },
  computed: {
    logicalNodes() {
      return buildNodes(this.checkpoints, this.participant)
    },
    /**
     * Closed loop when the first and last checkpoint share the same physical
     * point identifier (e.g. start & finish both sit on the same reader).
     * At least 4 total nodes (≥ 3 unique positions) are required so a ring
     * layout actually looks like a ring.
     */
    isClosedLoop() {
      const nodes = this.logicalNodes
      if (nodes.length < 4) return false
      const first = normalizePoint(nodes[0].point)
      const last = normalizePoint(nodes[nodes.length - 1].point)
      if (!first || !last) return false
      return first === last
    },
    /**
     * Number of distinct drawn positions on the layout. For a closed loop the
     * last node reuses the first node's position, so we have N-1 positions.
     */
    positionCount() {
      const count = this.logicalNodes.length
      if (this.isClosedLoop) return Math.max(0, count - 1)
      return count
    },
    effectiveNodesPerRow() {
      const count = this.positionCount
      if (count === 0) return 1
      if (count <= this.maxNodesPerRow) return count
      return Math.max(4, Math.min(this.maxNodesPerRow, Math.ceil(Math.sqrt(count * 2))))
    },
    viewHeight() {
      if (this.isClosedLoop) {
        const count = this.positionCount
        if (count >= 6) return 520
        if (count >= 4) return 460
        return 400
      }
      const count = this.logicalNodes.length
      if (count === 0) return 220
      const rows = Math.max(1, Math.ceil(count / this.effectiveNodesPerRow))
      return Math.max(260, this.padding * 2 + (rows - 1) * this.rowSpacing)
    },
    /**
     * Geometric positions for `positionCount` distinct spots.
     * Closed-loop → ellipse; otherwise → serpentine.
     */
    layoutPoints() {
      const count = this.positionCount
      if (this.isClosedLoop) {
        return buildEllipseLayout(count, this.viewWidth, this.viewHeight, this.padding)
      }
      return buildSerpentineLayout(
        count,
        this.viewWidth,
        this.viewHeight,
        this.padding,
        this.effectiveNodesPerRow,
      )
    },
    /**
     * Enrich each logical node with its on-canvas position.
     * In closed-loop mode the last node maps to position 0 so start and
     * finish overlap on the ring; we flag it to hide its circle visual and
     * merge labels into the first node.
     */
    orderedNodes() {
      const nodes = this.logicalNodes
      const closed = this.isClosedLoop
      return nodes.map((n, i) => {
        const posIdx = (closed && i === nodes.length - 1) ? 0 : i
        const pt = this.layoutPoints[posIdx] || { x: 0, y: 0 }
        return {
          ...n,
          x: pt.x,
          y: pt.y,
          isMergedEnd: !!(closed && i === nodes.length - 1),
        }
      })
    },
    /**
     * In closed-loop mode the first visible node represents both start and
     * finish, so combine their labels, take the most advanced state of the
     * two, and preserve badges from whichever is active.
     */
    visibleNodes() {
      const all = this.orderedNodes
      if (!this.isClosedLoop || all.length === 0) return all
      const last = all[all.length - 1]
      const first = { ...all[0] }
      first.mergedHeader = first.header === last.header
        ? first.header
        : `${first.header} / ${last.header}`
      first.mergedActualTimeLabel = last.actualTimeLabel || first.actualTimeLabel
      // If finish has a reading, surface finish state (green/violation/etc.)
      // as it is the most advanced side of the loop.
      if (last.hasReading) {
        first.state = last.state
        first.stateClass = last.stateClass
        first.badgeText = last.badgeText || first.badgeText
        first.badgeVariant = last.badgeVariant || first.badgeVariant
      }
      return [first, ...all.slice(1, -1)]
    },
    routePath() {
      if (this.isClosedLoop) {
        return buildClosedLoopPath(this.layoutPoints)
      }
      return buildSmoothPath(this.orderedNodes)
    },
    activeNodeIndex() {
      for (let i = this.orderedNodes.length - 1; i >= 0; i -= 1) {
        if (this.orderedNodes[i].hasReading) return i
      }
      return -1
    },
    completedPath() {
      const idx = this.activeNodeIndex
      if (idx <= 0) return ''
      if (this.isClosedLoop) {
        return buildClosedLoopPartialPath(this.layoutPoints, idx)
      }
      return buildSmoothPath(this.orderedNodes.slice(0, idx + 1))
    },
    progressFractionStatic() {
      const idx = this.activeNodeIndex
      const total = this.orderedNodes.length
      if (idx < 0 || total <= 1) return 0
      return idx / (total - 1)
    },
    lapSeparators() {
      if (this.isClosedLoop) return []
      const seps = []
      for (let i = 1; i < this.orderedNodes.length; i += 1) {
        const prev = this.orderedNodes[i - 1]
        const cur = this.orderedNodes[i]
        if ((prev.lap || 0) !== (cur.lap || 0)) {
          const x = (prev.x + cur.x) / 2
          seps.push({
            index: i,
            x,
            yTop: Math.min(prev.y, cur.y) - 40,
            yBottom: Math.max(prev.y, cur.y) + 40,
            toLap: cur.lap || 0,
          })
        }
      }
      return seps
    },
    /**
     * Checkpoints that share a physical chip reader (same normalized `point`)
     * with another checkpoint somewhere else on the track — e.g. an
     * out-and-back where TRZT1 is passed at 1km and 4km by the same antenna.
     * Returned as a map: nodeIndex → { key, total, order } so the template
     * can decorate the node with a "shared reader" hint.
     *
     * In closed-loop mode the start/finish duplicate is excluded (it's
     * already merged into a single visual node).
     */
    sharedReaderInfo() {
      const info = Object.create(null)
      const nodes = this.logicalNodes
      if (!nodes || nodes.length === 0) return info
      const effective = this.isClosedLoop
        ? nodes.slice(0, Math.max(0, nodes.length - 1))
        : nodes
      const buckets = new Map()
      effective.forEach((n, i) => {
        const key = normalizePoint(n.point)
        if (!key) return
        if (!buckets.has(key)) buckets.set(key, [])
        buckets.get(key).push(i)
      })
      buckets.forEach((indices, key) => {
        if (indices.length < 2) return
        indices.forEach((nodeIdx, k) => {
          info[nodeIdx] = {
            key,
            total: indices.length,
            order: k + 1,
          }
        })
      })
      return info
    },
    /**
     * Dashed SVG connector lines between positions that share a reader.
     * Drawn behind the main route to hint the physical co-location without
     * cluttering the ring shape.
     */
    sharedReaderLinks() {
      const links = []
      const nodes = this.logicalNodes
      if (!nodes || nodes.length === 0) return links
      const lastPositionIdx = this.isClosedLoop
        ? Math.max(0, nodes.length - 2)
        : nodes.length - 1
      const buckets = new Map()
      for (let i = 0; i <= lastPositionIdx; i += 1) {
        const n = nodes[i]
        const key = normalizePoint(n && n.point)
        if (!key) continue
        if (!buckets.has(key)) buckets.set(key, [])
        buckets.get(key).push(i)
      }
      buckets.forEach((indices, key) => {
        if (indices.length < 2) return
        for (let i = 0; i < indices.length - 1; i += 1) {
          const aIdx = indices[i]
          const bIdx = indices[i + 1]
          const a = this.layoutPoints[aIdx]
          const b = this.layoutPoints[bIdx]
          if (!a || !b) continue
          links.push({
            key: `${key}-${aIdx}-${bIdx}`,
            x1: a.x,
            y1: a.y,
            x2: b.x,
            y2: b.y,
          })
        }
      })
      return links
    },
  },
  watch: {
    participant() {
      this.stopAnimation()
      this.$nextTick(() => this.refreshMarker())
    },
    checkpoints() {
      this.stopAnimation()
      this.$nextTick(() => this.refreshMarker())
    },
    routePath() {
      this.$nextTick(() => this.refreshMarker())
    },
    progressFractionStatic() {
      this.$nextTick(() => this.refreshMarker())
    },
  },
  mounted() {
    this.$nextTick(() => this.refreshMarker())
  },
  beforeDestroy() {
    this.stopAnimation()
  },
  methods: {
    truncate(text, max) {
      const s = String(text == null ? '' : text)
      if (s.length <= max) return s
      return `${s.slice(0, Math.max(0, max - 1))}…`
    },
    /**
     * Compute the marker position directly from the rendered SVG path using
     * `getPointAtLength`, so the runner follows the real curve (Bezier,
     * ellipse) instead of chord lines between node centers.
     */
    refreshMarker() {
      if (this.orderedNodes.length <= 1) {
        this.markerXY = null
        return
      }
      const t = this.isAnimating ? this.animationT : this.progressFractionStatic
      if (t <= 0) {
        this.markerXY = null
        return
      }
      const el = this.$refs.routePathEl
      if (el && typeof el.getTotalLength === 'function') {
        const total = el.getTotalLength()
        if (Number.isFinite(total) && total > 0) {
          const pt = el.getPointAtLength(Math.max(0, Math.min(1, t)) * total)
          this.markerXY = { x: pt.x, y: pt.y }
          return
        }
      }
      this.markerXY = interpolateAlong(this.orderedNodes, t)
    },
    startAnimation() {
      if (this.orderedNodes.length < 2) return
      const target = this.progressFractionStatic > 0 ? this.progressFractionStatic : 1
      this.stopAnimation()
      this.isAnimating = true
      this.animationStartTs = null
      const step = (ts) => {
        if (!this.isAnimating) return
        if (this.animationStartTs == null) this.animationStartTs = ts
        const elapsed = ts - this.animationStartTs
        const dur = Math.max(600, this.animationDurationMs * (target || 1))
        const t = Math.min(1, elapsed / dur) * target
        this.animationT = t
        this.refreshMarker()
        if (elapsed >= dur) {
          this.isAnimating = false
          this.animationT = target
          this.animationHandle = null
          this.refreshMarker()
          return
        }
        this.animationHandle = requestAnimationFrame(step)
      }
      this.animationHandle = requestAnimationFrame(step)
    },
    stopAnimation() {
      if (this.animationHandle != null) {
        cancelAnimationFrame(this.animationHandle)
        this.animationHandle = null
      }
      this.isAnimating = false
      this.animationT = 0
      this.refreshMarker()
    },
  },
}
</script>

<style scoped>
.track-schema {
  border: 1px solid #ebe9f1;
  border-radius: 0.5rem;
  background: #fff;
  padding: 0.65rem 0.75rem 0.5rem;
  margin-bottom: 0.75rem;
}

.track-schema__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.35rem;
  margin-bottom: 0.5rem;
}

.track-schema__title {
  font-weight: 600;
  color: #3f3b4f;
  display: flex;
  align-items: center;
  font-size: 0.95rem;
}

.track-schema__controls {
  display: flex;
  gap: 0.35rem;
}

.track-schema__beta-badge {
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-size: 0.7rem;
}

.track-schema__beta-note {
  margin: 0 0 0.5rem;
  padding: 0.45rem 0.6rem;
  background: rgba(255, 159, 67, 0.08);
  border: 1px solid rgba(255, 159, 67, 0.3);
  border-left: 3px solid #ff9f43;
  border-radius: 0.3rem;
  font-size: 0.8125rem;
  color: #814a10;
  line-height: 1.4;
}

.track-schema__empty {
  text-align: center;
  color: #7c7a88;
  padding: 1.25rem 0.5rem;
}

.track-schema__canvas-wrap {
  position: relative;
}

.track-schema__svg {
  width: 100%;
  height: auto;
  display: block;
  background: linear-gradient(180deg, #fbfbfd 0%, #f6f6fa 100%);
  border-radius: 0.4rem;
  border: 1px solid #ececf2;
}

.track-schema__path-base {
  stroke: #c8c7d4;
  stroke-width: 4;
  stroke-dasharray: 8 8;
  stroke-linecap: round;
}

.track-schema__reader-link {
  stroke: #a39be6;
  stroke-width: 1.2;
  stroke-dasharray: 2 4;
  opacity: 0.55;
}

.track-schema__reader-hint {
  fill: #7367f0;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.2px;
  opacity: 0.85;
}

.track-schema__path-done {
  stroke: #28c76f;
  stroke-width: 5;
  stroke-linecap: round;
  filter: drop-shadow(0 1px 2px rgba(40, 199, 111, 0.25));
}

.track-schema__lap-sep {
  stroke: #b9b9c3;
  stroke-width: 1;
  stroke-dasharray: 3 4;
}

.track-schema__lap-label {
  fill: #7c7a88;
  font-size: 10px;
  font-weight: 600;
}

.track-schema__node {
  fill: #fff;
  stroke: #9c9ab0;
  stroke-width: 2;
  transition: fill 0.2s ease, stroke 0.2s ease;
}

.track-schema__node-index {
  fill: #3f3b4f;
  font-size: 11px;
  font-weight: 700;
}

.track-schema__node-label {
  fill: #3f3b4f;
  font-size: 11px;
  font-weight: 600;
}

.track-schema__node-sub {
  fill: #7c7a88;
  font-size: 10px;
  font-weight: 500;
}

.track-schema__node-time {
  fill: #5e50ce;
  font-size: 10px;
  font-weight: 600;
}

.track-schema__node-actual {
  fill: #17805a;
  font-size: 10px;
  font-weight: 600;
}

.track-schema__node-badge {
  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.track-schema__node-badge--overridden { fill: #ff9f43; }
.track-schema__node-badge--violation { fill: #ea5455; }
.track-schema__node-badge--missing { fill: #b95000; }

.track-schema__state-done .track-schema__node { fill: #28c76f; stroke: #1e9c5e; }
.track-schema__state-done .track-schema__node-index { fill: #fff; }

.track-schema__state-current .track-schema__node { fill: #00cfe8; stroke: #009bb5; }
.track-schema__state-current .track-schema__node-index { fill: #fff; }

.track-schema__state-violation .track-schema__node { fill: #ea5455; stroke: #b03e3e; }
.track-schema__state-violation .track-schema__node-index { fill: #fff; }

.track-schema__state-violation-current .track-schema__node { fill: #ea5455; stroke: #b03e3e; }
.track-schema__state-violation-current .track-schema__node-index { fill: #fff; }

.track-schema__state-overridden .track-schema__node { fill: #ff9f43; stroke: #d37711; }
.track-schema__state-overridden .track-schema__node-index { fill: #fff; }

.track-schema__state-missing .track-schema__node { fill: #fff; stroke: #ea5455; stroke-dasharray: 4 3; }
.track-schema__state-missing .track-schema__node-index { fill: #ea5455; }

.track-schema__state-pending .track-schema__node { fill: #fff; stroke: #b9b9c3; }
.track-schema__state-pending .track-schema__node-index { fill: #7c7a88; }

.track-schema__node-group.is-merged .track-schema__node {
  stroke-width: 3;
}

.track-schema__node-group.is-merged .track-schema__node-index {
  font-size: 12px;
}

.track-schema__node-pulse {
  fill: rgba(0, 207, 232, 0.18);
  stroke: rgba(0, 207, 232, 0.6);
  stroke-width: 1.5;
  transform-box: fill-box;
  transform-origin: center;
  animation: track-schema-pulse 1.6s ease-in-out infinite;
}

@keyframes track-schema-pulse {
  0% { opacity: 0.9; transform: scale(0.85); }
  50% { opacity: 0.3; transform: scale(1.25); }
  100% { opacity: 0.9; transform: scale(0.85); }
}

.track-schema__runner-halo {
  fill: rgba(115, 103, 240, 0.18);
  stroke: rgba(115, 103, 240, 0.45);
  stroke-width: 1.5;
}

.track-schema__runner-dot {
  fill: #7367f0;
  stroke: #fff;
  stroke-width: 2;
  filter: drop-shadow(0 2px 3px rgba(115, 103, 240, 0.45));
}

.track-schema__legend {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  padding: 0.4rem 0.2rem 0;
  font-size: 0.8125rem;
  color: #5e5873;
}

.track-schema__legend-item {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
}

.dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  border: 2px solid transparent;
}

.dot--done { background: #28c76f; border-color: #1e9c5e; }
.dot--current { background: #00cfe8; border-color: #009bb5; }
.dot--pending { background: #fff; border-color: #b9b9c3; }
.dot--missing { background: #fff; border-color: #ea5455; border-style: dashed; }
.dot--violation { background: #ea5455; border-color: #b03e3e; }
.dot--overridden { background: #ff9f43; border-color: #d37711; }

.legend-dash {
  display: inline-block;
  width: 18px;
  height: 0;
  border-top: 2px dashed #a39be6;
  opacity: 0.85;
}
</style>
